import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CloseIcon from '../../../images/close.svg';
import ArrowIcon from '../../../images/arrow.svg';
import { MenuItemFieldsFragment } from '@src/generated/graphql';
import DynamicLink from '../../shared/DynamicLink';

type Logo = {
  logoImage: {
    url: string;
    description: string;
    title: string;
    link: string;
  };
  logoName: string;
};

type CallToAction = {
  buttonText: string;
  buttonLink: string;
};

type MobileMenuProps = {
  isOpen: boolean;
  logo: Logo[];
  callToAction?: CallToAction[];
  style?: React.CSSProperties;
  menuItems?: MenuItemFieldsFragment[];
  subMenuItems?: any;
  onClose: () => void;
};

const MobileMenu = ({
  isOpen,
  onClose,
  logo,
  callToAction,
  style,
  menuItems,
  subMenuItems,
}: MobileMenuProps) => {
  const mobileLogo = logo[1];
  const [currentMenu, setCurrentMenu] = useState<MenuItemFieldsFragment | null>(null);
  const resolvedStyle = { ...style, color: "#000" };
  
  useEffect(() => {
    if (!isOpen) {
      setCurrentMenu(null);
    }
  }, [isOpen]);

  const handleMenuClick = (menuItem: MenuItemFieldsFragment) => {
    const filteredSubMenuItems = subMenuItems?.filter(
      subItem => subItem.parentItem?.sys?.id === menuItem.sys.id,
    );

    if (filteredSubMenuItems?.length > 0) {
      setCurrentMenu(menuItem);
    } else {
      onClose();
    }
  };

  const handleSubMenuClick = () => {
    onClose();
  };

  const handleBackClick = () => {
    setCurrentMenu(null);
  };

  const handleClose = () => {
    setCurrentMenu(null);
    onClose();
  };

  // Filter submenu items to only show children of the current menu item
  const currentSubMenuItems = currentMenu 
    ? subMenuItems?.filter(subItem => subItem.parentItem?.sys?.id === currentMenu.sys.id)
    : [];

  return (
    <div className={`mobile-menu-overlay ${isOpen ? 'open' : ''}`}>
      <div className="mobile-menu-header">
        {currentMenu ? (
          <button className="back-button" onClick={handleBackClick}>
            <img src={ArrowIcon} alt="Back" className="arrow-icon" />
            Back
          </button>
        ) : (
          <Link to="/" className="mobile-menu-logo-container">
            <img
              src={mobileLogo.logoImage.url} alt={mobileLogo.logoImage.title}
              className="mobile-menu-logo"
            />
          </Link>
        )}

        <button className="close-button" onClick={handleClose}>
          <img src={CloseIcon} alt="Close" className="close-icon" />
        </button>
      </div>

      <div className="header-divider" />

      <nav className={`mobile-menu ${currentMenu ? 'submenu-open' : 'main-menu'}`}>
        {currentMenu
          ? currentSubMenuItems?.map(subItem => (
              <li key={subItem.id} className="mobile-nav-item">
                <DynamicLink
                  url={subItem?.linkText?.linkUrl ?? `/products/${subItem.slug}`}
                  style={resolvedStyle}
                  onClick={handleSubMenuClick}
                >
                  <>
                    <span className="drop-nav-icon">
                      <img
                        src={subItem?.icon?.url}
                        alt={subItem?.icon?.title}
                      />
                    </span>
                    {subItem?.title}
                  </>
                </DynamicLink>
              </li>
            ))
          : menuItems &&
            menuItems?.length > 0 &&
            menuItems
              .filter(menuItem => menuItem.orderposition !== null)
              .sort((a, b) => (a.orderposition || 0) - (b.orderposition || 0))
              .map(menuItem => {
                const filteredSubMenuItems = subMenuItems?.filter(
                  subItem => subItem.parentItem?.sys?.id === menuItem.sys.id,
                );

                return (
                  <li key={menuItem.sys.id} className="mobile-nav-item">
                    <DynamicLink
                      url={menuItem?.linkText?.linkUrl ?? menuItem?.slug}
                      target={menuItem.openInNewTab ? '_blank' : '_self'}
                      style={resolvedStyle}
                      onClick={() => {
                        onClose();
                      }}
                    >
                      {menuItem?.title}
                    </DynamicLink>
                    {filteredSubMenuItems?.length > 0 && (
                      <img
                        src={ArrowIcon}
                        alt="Arrow"
                        className="submenu-arrow arrow-icon"
                        onClick={() => handleMenuClick(menuItem)}
                      />
                    )}
                  </li>
                );
              })}

        <div className="header-right">
          {callToAction?.[0]?.buttonLink && (
            <DynamicLink
              url={callToAction?.[0]?.buttonLink}
              className="links"
            >
              <span className="white-color-mobile">{callToAction?.[0].buttonText}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="6"
                height="9"
                viewBox="0 0 6 9"
                fill="none"
              >
                <path
                  d="M1 7.75L3.78571 4.5L1 1.25"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </DynamicLink>
          )}
          {callToAction?.[1]?.buttonLink && (
            <DynamicLink
              url={callToAction?.[1].buttonLink}
              className="btn-white"
            >
              <span>{callToAction?.[1].buttonText}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="9"
                viewBox="0 0 12 9"
                fill="none"
                className="btn-white-svg"
              >
                <path d="M1 7.75L3.78571 4.5L1 1.25" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="9"
                viewBox="0 0 12 9"
                fill="none"
                className="btn-white-svg-hover links-svg-hover"
              >
                <path d="M1 4.5h7" strokeWidth="2" strokeLinecap="round" />
                <path
                  d="M1 7.75L3.78571 4.5L1 1.25"
                  id="animated-path"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </DynamicLink>
          )}
        </div>
      </nav>
    </div>
  );
};

export default MobileMenu;